import throttle from 'lodash/throttle';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { grey10 } from '@zocdoc/branding/colors';
import { breakpoint, Breakpoints, getWindowWidth } from '@zocdoc/components/src/grid';
import { Close } from '@zocdoc/components/src/icons';
import * as Layout from '@zocdoc/components/src/Layout';
import { FontFamily } from '@zocdoc/components/src/typography';
import { Overlay } from '../Overlay';
import * as TrackingConstants from '../../EventTrackingConstants';
import LocationCardCarousel from '../LocationCardCarousel';
import { CarouselMapWithTheme } from './CarouselMap';

const DEFAULT_CAROUSEL_HEIGHT = 201;
const HEADER_HEIGHT = 56;

export class CarouselMapView extends Component {
    static propTypes = {
        hideMap: PropTypes.func,
        locations: PropTypes.arrayOf(PropTypes.object),
        locationId: PropTypes.string.isRequired,
        setLocation: PropTypes.func.isRequired,
    };

    carouselRef = React.createRef();
    carouselHeight = null;
    mapRef = React.createRef();

    state = {
        isDesktop: null,
    };

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = throttle(() => {
        const isDesktop = getWindowWidth() >= Breakpoints.medium;

        if (this.state.isDesktop !== isDesktop) {
            this.setState({ isDesktop }, this.centerMap());
        } else {
            this.centerMap();
        }
    }, 500)

    centerMap = () => { if (this.mapRef.current) this.mapRef.current.recenterMap(); }

    render() {
        const {
            label,
            locations,
            hideMap,
        } = this.props;

        if (!locations || locations.length === 0 || this.state.isDesktop === null) return <div />;

        const mapLocations = locations.map(loc => {
            return {
                ...loc,
                location: {
                    latitude: loc.latitude,
                    longitude: loc.longitude,
                },
            };
        });

        return this.state.isDesktop ? (
            <Overlay onClickClose={hideMap}>
                {this.renderMapAndCarousel(mapLocations)}
            </Overlay>
        ) : (
            <MobileMapOverlay>
                <MobileMapHeader>
                    <MobileMapHeaderLabel>{label}</MobileMapHeaderLabel>
                    <MapTapWrapper
                        onClick={hideMap}
                    >
                        <MapCloseIcon color="currentColor" />
                    </MapTapWrapper>
                </MobileMapHeader>
                {this.renderMapAndCarousel(mapLocations)}
            </MobileMapOverlay>
        );
    }

    renderMapAndCarousel(locations) {
        if (this.carouselHeight === null) {
            this.carouselHeight = this.carouselRef.current
                ? findDOMNode(this.carouselRef.current).clientHeight
                : DEFAULT_CAROUSEL_HEIGHT;
        }

        const { locationId, providerId, setLocation } = this.props;
        const carouselHeight = this.carouselHeight || DEFAULT_CAROUSEL_HEIGHT;
        const mobileMapHeightOffset = carouselHeight + HEADER_HEIGHT;

        return (
            <MapContainer>
                <StyledMap
                    data-test="carousel-styled-map"
                    entities={locations}
                    innerRef={this.mapRef}
                    locationId={locationId}
                    maxZoomLevel={17}
                    mobileMapHeightOffset={mobileMapHeightOffset}
                    selectedEntityId={locationId}
                    setLocation={setLocation}
                />

                <CarouselContainer
                    innerRef={this.carouselRef}
                >
                    <LocationCardCarousel
                        locationId={locationId}
                        locations={locations}
                        providerId={providerId}
                        section={TrackingConstants.TARGETS.PROFILE_MAP_LOCATION_CAROUSEL}
                        showMapThumbnail={false}
                    />
                </CarouselContainer>
            </MapContainer>
        );
    }
}

const MobileMapOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: auto;
    z-index: 12;
    background-color: ${grey10};
`;

const MobileMapHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: ${HEADER_HEIGHT}px;
    width: 100%;
    background-color: ${p => p.theme.header.bgColor};
    color: ${p => p.theme.header.textColor};
    font: 20px/28px ${FontFamily.SharpSansBold};
    padding-left: ${Layout.PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px;

    ${breakpoint.xsmall}
        padding-left: ${Layout.PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL}px;
    }

    ${breakpoint.small} {
        padding-left: ${Layout.PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
    }
`;

const MobileMapHeaderLabel = styled.p`
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MapCloseIcon = styled(Close)`
    font-size: 12px;
`;

const MapTapWrapper = styled.div`
    display: flex;
    width: 48px;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;

    ${breakpoint.small} {
        margin-right: 20px;
    }
`;

const MapContainer = styled.div`
    position: relative;
    height: calc(100vh - ${HEADER_HEIGHT}px);
    top: 0;
    width: 100%;
    left: 0;

    ${breakpoint.medium} {
        height: 80vh;
        top: 10vh;
        width: 80%;
        left: 10%;
    }
`;

const StyledMap = styled(CarouselMapWithTheme)`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const CarouselContainer = styled.div`
    padding: 8px 16px;
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 10px;

    ${breakpoint.small} {
        padding: 0;
        width: 400px;
        left: calc(50% - 200px);
        bottom: 20px;
    }

    ${breakpoint.medium} {
        bottom: 20px;
    }
`;
